import {
  Autocomplete,
  Box,
  Checkbox,
  Grid,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { TrashIcon, AddPlusIcon } from "../Common/icons";
import { disabledSx, checkboxSx, mobileCardSx } from "../Common/styles";
import FieldLabel from "../Common/FieldLabel";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import React from "react";
import { CommonType, ParsedRowType } from "../../types";

type ParsedDataTableProps = {
  parsed: ParsedRowType[];
  dataType: CommonType[];
  onStartChange: (value: string, itemId: string) => void;
  onEndChange: (value: string, itemId: string) => void;
  onParameterTypeChange: (value: string, itemId: string) => void;
  onTypeChange: (typeId: number, toTypeId: number | null, itemId: string) => void;
  onToTypeChange: (toTypeId: number, typeId: number | null, itemId: string) => void;
  onDescriptionChange: (value: string, itemId: string) => void;
  onIsDeviceIdChange: (value: boolean, itemId: string) => void;
  showDeviceId: boolean;
  onDeleteRow: (itemId: string) => void;
  onAddChildRow: (textValueConvert: string | undefined, itemId: string) => void;
  getDisplayIndex: (parsed: ParsedRowType[], itemId?: string | null, isParameterFlag?: boolean) => string;
};


const ParsedDataTable: React.FC<ParsedDataTableProps> = ({
  parsed,
  dataType,
  onStartChange,
  onEndChange,
  onParameterTypeChange,
  onTypeChange,
  onToTypeChange,
  onDescriptionChange,
  onIsDeviceIdChange,
  showDeviceId,
  onDeleteRow,
  onAddChildRow,
  getDisplayIndex,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  if (isMobile) {
    return (
      <Box sx={{ px: 2, py: 1, display: "flex", flexDirection: "column", gap: 1.5 }}>
        {parsed.map((code) => {
          const baseValue = code.baseId ? parsed.find((p) => p.id === code.baseId)?.textValueConvert : undefined;
          return (
            <Box
              key={code.id}
              sx={mobileCardSx}
            >
              {/* Card header */}
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                <Box>
                  <Typography fontWeight={600} fontSize={13}>
                    Index: {getDisplayIndex(parsed, code.id)}
                  </Typography>
                  {baseValue && (
                    <Typography variant="caption" sx={{ color: "text.secondary", fontStyle: "italic" }}>
                      {baseValue}
                    </Typography>
                  )}
                </Box>
                <Box sx={{ display: "flex", gap: 0.5 }}>
                  <IconButton size="small" onClick={() => onDeleteRow(code.id)}>
                    <TrashIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => onAddChildRow(code.textValueConvert, code.id)}
                    disabled={code.baseId != undefined}
                    sx={{ opacity: code.baseId != undefined ? 0.3 : 1 }}
                  >
                    <AddPlusIcon />
                  </IconButton>
                </Box>
              </Box>

              {/* Fields grid */}
              <Grid container spacing={1}>
                <Grid size={6}>
                  <FieldLabel>Başlangıç</FieldLabel>
                  <TextField fullWidth size="small" value={code.start ?? ""} error={Boolean(code.textError)} onChange={(e) => onStartChange(e.target.value, code.id)} />
                </Grid>
                <Grid size={6}>
                  <FieldLabel>Bitiş</FieldLabel>
                  <TextField fullWidth size="small" value={code.end ?? ""} error={Boolean(code.textError)} onChange={(e) => onEndChange(e.target.value, code.id)} />
                </Grid>
                <Grid size={6}>
                  <FieldLabel>Parametre</FieldLabel>
                  <TextField fullWidth size="small" value={code.parameterType} slotProps={{ htmlInput: { maxLength: 150 } }} onChange={(e) => onParameterTypeChange(e.target.value, code.id)} />
                </Grid>
                <Grid size={6}>
                  <FieldLabel>Parçalı Kod</FieldLabel>
                  <TextField fullWidth size="small" value={code.textValue} disabled sx={disabledSx} />
                </Grid>
                <Grid size={6}>
                  <FieldLabel>Tip</FieldLabel>
                  <Autocomplete
                    key={`type-${code.id}-${code.typeId ?? "none"}`}
                    fullWidth size="small"
                    value={dataType.find((dt) => dt.id === code.typeId) ?? undefined}
                    isOptionEqualToValue={(o, v) => o.id === v.id}
                    options={dataType}
                    getOptionLabel={(o) => o.name || ""}
                    onChange={(_, v) => v && onTypeChange(v.id, code.toTypeId ?? null, code.id)}
                    disableClearable
                    renderInput={(params) => <TextField {...params} />}
                  />
                </Grid>
                <Grid size={6}>
                  <FieldLabel>Convert Tip</FieldLabel>
                  <Autocomplete
                    key={`totype-${code.id}-${code.toTypeId ?? "none"}`}
                    fullWidth size="small"
                    value={dataType.find((dt) => dt.id === code.toTypeId) ?? undefined}
                    isOptionEqualToValue={(o, v) => o.id === v.id}
                    options={dataType}
                    getOptionLabel={(o) => o.name || ""}
                    onChange={(_, v) => v && onToTypeChange(v.id, code.typeId ?? null, code.id)}
                    disableClearable
                    renderInput={(params) => <TextField {...params} />}
                  />
                </Grid>
                <Grid size={6}>
                  <FieldLabel>Değer</FieldLabel>
                  <TextField fullWidth size="small" value={code.textValueConvert} disabled sx={disabledSx} />
                </Grid>
                <Grid size={6}>
                  <FieldLabel>Asıl Index</FieldLabel>
                  <TextField fullWidth size="small" value={code.baseId ? getDisplayIndex(parsed, code.baseId) : ""} disabled sx={disabledSx} />
                </Grid>
                <Grid size={showDeviceId ? 9 : 12}>
                  <FieldLabel>Açıklama</FieldLabel>
                  <TextField fullWidth size="small" value={code.description} slotProps={{ htmlInput: { maxLength: 150 } }} onChange={(e) => onDescriptionChange(e.target.value, code.id)} />
                </Grid>
                {showDeviceId && (
                  <Grid size={3} sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                    <FieldLabel>Device Id</FieldLabel>
                    <Checkbox
                      checked={code.isDeviceId ?? false}
                      icon={<RadioButtonUncheckedIcon />}
                      checkedIcon={<CheckCircleIcon />}
                      sx={checkboxSx("info.main")}
                      onChange={(e) => onIsDeviceIdChange(e.target.checked, code.id)}
                    />
                  </Grid>
                )}
              </Grid>
            </Box>
          );
        })}
      </Box>
    );
  }

  return (
    <Grid container direction="column" spacing={1} sx={{ pt: 2, px: 2, pb: 2 }}>
      <TableContainer className="app-table" sx={{ maxHeight: "50rem", overflowX: "auto" }}>
        <Table stickyHeader sx={{ tableLayout: "fixed", minWidth: 900 }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 30, pl: 0.5 }}>Index</TableCell>
              <TableCell sx={{ width: 40 }}>Başlangıç</TableCell>
              <TableCell sx={{ width: 40 }}>Bitiş</TableCell>
              <TableCell sx={{ width: 100 }}>Parametre</TableCell>
              <TableCell sx={{ width: 150 }}>Parçalı Kod</TableCell>
              <TableCell sx={{ width: 80 }}>Tip</TableCell>
              <TableCell sx={{ width: 80 }}>Convert Tip</TableCell>
              <TableCell sx={{ width: 150 }}>Değer</TableCell>
              <TableCell sx={{ width: 50 }}>Asıl Index</TableCell>
              <TableCell sx={{ width: 150 }}>Açıklama</TableCell>
              {showDeviceId && <TableCell sx={{ width: 60 }}>Device Id</TableCell>}
              <TableCell sx={{ width: 40 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {parsed.map((code) => (
              <React.Fragment key={code.id}>
                <TableRow>
                  <TableCell sx={{ width: 30 }}>
                    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
                      <Typography sx={{ textAlign: "left", lineHeight: 1.2 }}>
                        {getDisplayIndex(parsed, code.id)}
                      </Typography>
                      {code.baseId && parsed.find((p) => p.id === code.baseId) && (
                        <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.65rem", fontStyle: "italic", whiteSpace: "nowrap" }}>
                          {parsed.find((p) => p.id === code.baseId)?.textValueConvert}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell sx={{ width: 40 }}>
                    <TextField fullWidth size="small" value={code.start ?? ""} error={Boolean(code.textError !== "")} helperText={code.textError} onChange={(e) => onStartChange(e.target.value, code.id)} />
                  </TableCell>
                  <TableCell sx={{ width: 40 }}>
                    <TextField fullWidth size="small" value={code.end ?? ""} error={Boolean(code.textError !== "")} helperText={code.textError} onChange={(e) => onEndChange(e.target.value, code.id)} />
                  </TableCell>
                  <TableCell sx={{ width: 100 }}>
                    <TextField fullWidth size="small" value={code.parameterType} slotProps={{ htmlInput: { maxLength: 150 } }} error={Boolean(code.textError !== "")} helperText={code.textError} onChange={(e) => onParameterTypeChange(e.target.value, code.id)} />
                  </TableCell>
                  <TableCell sx={{ width: 150 }}>
                    <TextField size="small" fullWidth value={code.textValue} disabled sx={disabledSx} />
                  </TableCell>
                  <TableCell sx={{ width: 120 }}>
                    <Autocomplete
                      key={`type-${code.id}-${code.typeId ?? "none"}`}
                      fullWidth size="small"
                      value={dataType.find((dt) => dt.id === code.typeId) ?? undefined}
                      isOptionEqualToValue={(o, v) => o.id === v.id}
                      options={dataType}
                      getOptionLabel={(o) => o.name || ""}
                      onChange={(_, v) => v && onTypeChange(v.id, code.toTypeId ?? null, code.id)}
                      disableClearable
                      renderInput={(params) => <TextField {...params} sx={disabledSx} />}
                    />
                  </TableCell>
                  <TableCell sx={{ width: 120 }}>
                    <Autocomplete
                      key={`totype-${code.id}-${code.toTypeId ?? "none"}`}
                      fullWidth size="small"
                      value={dataType.find((dt) => dt.id === code.toTypeId) ?? undefined}
                      isOptionEqualToValue={(o, v) => o.id === v.id}
                      options={dataType}
                      getOptionLabel={(o) => o.name || ""}
                      onChange={(_, v) => v && onToTypeChange(v.id, code.typeId ?? null, code.id)}
                      disableClearable
                      renderInput={(params) => <TextField {...params} sx={disabledSx} />}
                    />
                  </TableCell>
                  <TableCell sx={{ width: 150 }}>
                    <TextField fullWidth size="small" value={code.textValueConvert} error={!code.textValueConvert || code.textValueConvert.trim() === ""} disabled sx={disabledSx} />
                  </TableCell>
                  <TableCell sx={{ width: 50 }}>
                    <TextField size="small" value={code.baseId ? getDisplayIndex(parsed, code.baseId) : ""} disabled sx={{ "& .MuiInputBase-input.Mui-disabled": { WebkitTextFillColor: "#555", fontWeight: 600 }, "& .MuiOutlinedInput-root.Mui-disabled": { backgroundColor: "#f2f2f2" } }} />
                  </TableCell>
                  <TableCell sx={{ width: 150 }}>
                    <TextField fullWidth size="small" value={code.description} slotProps={{ htmlInput: { maxLength: 150 } }} onChange={(e) => onDescriptionChange(e.target.value, code.id)} />
                  </TableCell>
                  {showDeviceId && (
                    <TableCell sx={{ width: 60, textAlign: "center" }}>
                      <Checkbox
                        checked={code.isDeviceId ?? false}
                        icon={<RadioButtonUncheckedIcon />}
                        checkedIcon={<CheckCircleIcon />}
                        sx={checkboxSx("info.main")}
                        onChange={(e) => onIsDeviceIdChange(e.target.checked, code.id)}
                      />
                    </TableCell>
                  )}
                  <TableCell sx={{ width: 50 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      <IconButton size="small" aria-label="delete" onClick={() => onDeleteRow(code.id)}>
                        <TrashIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        aria-label="add"
                        onClick={() => onAddChildRow(code.textValueConvert, code.id)}
                        disabled={code.baseId != undefined}
                        sx={{ opacity: code.baseId != undefined ? 0.3 : 1 }}
                      >
                        <AddPlusIcon />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Grid>
  );
};

export default ParsedDataTable;
